import React, { useState } from "react";
import { TrashIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import ModalUpdateSubscription from "./subscription_update_modal";
import Swal from "sweetalert2";
import { calculateTotalCost } from "../services/utils";

const SubscriptionManager = ({ 
    token, 
    customerPortal, 
    onBack, 
    activeEmployeesNumber, 
    subscriptionDetails,
    onDelete,
    customerType,
    tiers,
 }) => {
    const subscriptionStatusMap = {
        trialing: 'In prova',
        active: 'Attivo',
        incomplete: 'Incompleto',
        incomplete_expired: 'Incompleto scaduto',
        past_due: 'Pagamento insoluto',
        canceled: 'Cancellato',
        unpaid: 'Non pagato',
        cancel_at_period_end: 'Cancellato alla fine del periodo',
        disputed: 'Contestato',
    };

    const [editing, setEditing] = useState(null); 
    const [newQuantity, setNewQuantity] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newPrice, setNewPrice] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [updatedQuantity, setUpdatedQuantity] = useState(null);

    const handleSetEditing = (id, currentQuantity) => {
        setEditing(id);
        setNewQuantity(currentQuantity);
    };

    const handleUpdateQuantity = (id) => {
        const numericQuantity = parseInt(newQuantity, 10);   
        const subscription = subscriptionDetails.find((sub) => sub.id === id);

        if (!subscription) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Dettagli della sottoscrizione non trovati.',
                confirmButtonText: 'OK',
            });
            return;
        }
    
        const currentQuantity = subscription.quantity;
    
        if (isNaN(numericQuantity)) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Inserisci un numero valido.',
                confirmButtonText: 'OK',
            });
            return;
        }
    
        if (numericQuantity === currentQuantity) {
            Swal.fire({
                icon: 'info',
                title: 'Numero di licenze invariato',
                html: `La quantità inserita, <strong>${numericQuantity}</strong>, è uguale alla quantità attuale.`,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });
            return;
        }
    
        if (numericQuantity < activeEmployeesNumber) {
            Swal.fire({
                icon: 'error',
                title: 'Numero di licenze insufficiente',
                html: `Hai <strong>${activeEmployeesNumber}</strong> contratti attivi.<br><br> Non è possibile ridurre il numero di licenze sotto il numero di contratti attivi.`,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });
            return;
        }
        
        setSelectedPlan(subscription);
        setNewQuantity({
            id,
            quantity: numericQuantity,
        });
        
        const tiersArray = tiers[id]?.tiers;
        
        if (!tiers || tiers.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Tiers non trovati per la sottoscrizione selezionata.',
                confirmButtonText: 'OK',
            });
            return;
        }        
        const newPriceResult = calculateTotalCost(numericQuantity, tiersArray, customerType);
        
        setNewPrice(newPriceResult);
        
        setIsModalOpen(true);
        setEditing(null);
    };

    const handleCancelEdit = () => {
        setEditing(null);
    };

    const handleUpdateEmployeesNumber = (newQuantity) => {
        setUpdatedQuantity(newQuantity);
    };

    return (
        <>
            <div className="flex justify-end mt-4">
                <button
                    className="border block rounded-md px-3 py-2 mr-6 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600"
                    onClick={onBack}
                >
                    Torna indietro
                </button>
            </div>
            <div className="m-6 p-8 bg-white border border-gray-200 rounded-lg shadow-lg vh-100">
                <dl className="divide-y divide-gray-200">
                    {subscriptionDetails?.length > 0 ? (
                        subscriptionDetails.map((detail, index) => (
                            <div key={index} className="mb-8">
                                <div className="px-4 pb-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-md font-bold text-indigo-800 uppercase">Piano</dt>
                                    <dd className="mt-1 text-md font-bold text-indigo-800 leading-6 sm:col-span-2 sm:mt-0 flex justify-start">
                                        {detail?.plan?.name || ''}
                                    </dd>
                                </div>

                                <div className="border-t border-gray-100">
                                    <dl className="divide-y divide-gray-200">
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">Stato</dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0">
                                                {subscriptionStatusMap[detail.status]?.toUpperCase() || ''}
                                            </dd>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0 flex justify-start items-center">
                                                {detail.status === 'active' && (
                                                    <button
                                                        onClick={() => onDelete(detail.id)}
                                                        className="inline-flex border border-gray-500 rounded-md items-center px-3 py-1.5 font-semibold text-grey-800 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-red-600 hover:text-white hover:border-transparent"
                                                    >
                                                        Elimina
                                                        <TrashIcon className="h-5 w-5 ml-2" />
                                                    </button>
                                                )}
                                            </dd>
                                        </div>
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">Codice Sottoscrizione</dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 flex justify-start">
                                                {detail?.stripe_subscription_id || ''}
                                            </dd>
                                        </div>
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">Prezzo</dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0">
                                                € {detail?.price !== undefined ? detail?.price : '0'}
                                            </dd>
                                            {detail?.coupon && (
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0 flex justify-start items-center">
                                                   <span className="text-sm font-medium text-gray-900 uppercase">SCONTO&nbsp;</span> {detail?.coupon}
                                                </dd>
                                            )}
                                        </div>
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">Durata Mesi</dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 flex justify-start">
                                                {detail?.plan?.duration_months || ''}
                                            </dd>
                                        </div>
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">
                                                {detail?.status === 'trialing'
                                                    ? 'Termine prova'
                                                    : detail?.status === 'cancel_at_period_end'
                                                    ? 'Termine del periodo'
                                                    : 'Prossimo Rinnovo'}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 flex justify-start">
                                                {detail.current_period_end ? moment(detail.current_period_end).format('DD/MM/YYYY') : ''}
                                            </dd>
                                        </div>
                                        <div className="px-4 pt-6 pb-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                                            <dt className="text-sm font-medium text-gray-900 uppercase">Numero Licenze Dipendenti</dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0">
                                                {updatedQuantity !== null ? updatedQuantity : detail.quantity}
                                            </dd>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0 flex justify-start items-center">
                                                {detail.status === 'active' && editing !== detail.id && (
                                                    <button
                                                        onClick={() => handleSetEditing(detail.id, detail.quantity)}
                                                        className="inline-flex border border-gray-500 rounded-md items-center px-3 py-1.5 font-semibold text-grey-800 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-indigo-600 hover:text-white hover:border-transparent"
                                                    >
                                                        Modifica
                                                        <PencilSquareIcon className="h-5 w-5 ml-2" />
                                                    </button>
                                                )}
                                            </dd>

                                            {editing === detail.id && (
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0"></dd>
                                            )}
                                        </div>

                                        {editing === detail.id && (
                                            <div className="px-4 pt-6 pb-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                                                <dt className="text-sm font-medium text-gray-900 uppercase">Inserisci la nuova quantità</dt>

                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0">
                                                <input 
                                                    type="number" 
                                                    value={newQuantity} 
                                                    onChange={(e) => {
                                                        const value = parseInt(e.target.value, 10);
                                                        if (isNaN(value)) {
                                                            setNewQuantity('');
                                                        } else if (value < 1) {
                                                            setNewQuantity(1);
                                                        } else if (value > 100) {
                                                            setNewQuantity(100);
                                                        } else {
                                                            setNewQuantity(value);
                                                        }
                                                    }}
                                                    className="border border-gray-300 rounded-md px-2 py-1 text-sm w-20"
                                                    min="1"
                                                    max="100"
                                                    autoComplete="one-time-code"
                                                />
                                                </dd>

                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-1 sm:mt-0 flex justify-start items-center">
                                                    <button
                                                        onClick={() => handleUpdateQuantity(detail.id)}
                                                        className="me-3 inline-flex items-center justify-center border border-gray-500 rounded-md px-3 py-1.5 font-semibold text-grey-800 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-indigo-600 hover:text-white hover:border-transparent w-24"
                                                    >
                                                        Conferma
                                                    </button>
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="inline-flex items-center justify-center border border-gray-500 rounded-md px-3 py-1.5 font-semibold text-grey-800 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-gray-200 hover:text-gray-800 hover:border-transparent w-24"
                                                    >
                                                        Annulla
                                                    </button>
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-md font-semibold text-gray-500 text-center">Nessuna sottoscrizione trovata</p>
                    )}
                </dl>
            </div>
            <div className="m-6 bg-white border border-gray-200 rounded-lg shadow-lg">
                <div className="py-4 flex justify-center">
                    <a 
                        href={customerPortal} 
                        rel="noopener noreferrer" 
                        className="justify-center bg-indigo-600 text-white font-semibold px-4 py-2 rounded-md text-center hover:bg-indigo-500"
                    >
                        Gestisci Metodo di Pagamento
                    </a>   
                </div>
            </div>

            {isModalOpen && newQuantity && (
                <ModalUpdateSubscription
                    mode='updateSubscription'
                    token={token} 
                    selectedPlan={selectedPlan}
                    newQuantity={newQuantity} 
                    onClose={() => setIsModalOpen(false)}
                    newPrice={newPrice}
                    onUpdateQuantity={handleUpdateEmployeesNumber}
                />
            )}
            {/* <ModalUpdateSubscription
                    mode='newSubscription'
                    selectedPlan={selectedPlan}
                    employeeCount={employeeCount}
                    duration={duration}
                    selectedPrice={selectedPrice}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    onClose={handleCloseModal}
                    token={token}
                /> */}
        </>
    );
};

export default SubscriptionManager;
